/**
 * runidea.ai — Phase 15 — Scenarios tab.
 *
 * Lives inside DealDetail. Two halves:
 *   1. 3-column compare (Asking | Counter-Offer | Target Yield)
 *   2. Reverse-yield pill calculator: pick a target % → see the max
 *      purchase price to hit it across two horizons (Y1-5 & Y5-10).
 *
 * Calls POST /api/deals/{id}/analyze-scenarios on mount and again when
 * the user clicks "Recalculate" with edited asking / counter-offer.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import VerdictBadge from "@/components/VerdictBadge";
import ReverseRentCard from "@/components/deal/ReverseRentCard";
import { useFmt } from "@/lib/useFmt";
import { formatPsfShort } from "@/lib/priceNormalisation";
import { CalculatorIcon, Loader2, Sparkles, ArrowRight, Info } from "lucide-react";

const DEFAULT_YIELDS = [6, 7, 8, 10, 12];

function fmtPct(v, digits = 2) {
  if (v == null || isNaN(v)) return "—";
  return `${Number(v).toFixed(digits)}%`;
}

function bestColumn(rows, kpiKey, higherIsBetter = true) {
  let best = null, bestVal = null;
  rows.forEach((r, i) => {
    if (!r) return;
    const v = r.kpis?.[kpiKey];
    if (v == null) return;
    if (bestVal == null || (higherIsBetter ? v > bestVal : v < bestVal)) {
      bestVal = v; best = i;
    }
  });
  return best;
}

function CompareCell({ value, isBest, formatter, className = "" }) {
  return (
    <td
      className={`px-3 py-2.5 text-right tabular-nums ${className} ${
        isBest ? "bg-emerald-50 font-semibold text-emerald-700" : "text-slate-700"
      }`}
    >
      {formatter(value)}
    </td>
  );
}

function CompareHeader({ label, sublabel, price, fmt, testid }) {
  return (
    <th
      data-testid={testid}
      className="px-3 py-3 text-right align-top text-xs font-medium uppercase tracking-wider text-slate-500"
    >
      <div className="text-[10px] font-semibold text-slate-400">{label}</div>
      <div className="mt-0.5 text-base font-bold text-slate-900 normal-case tracking-normal">
        {fmt.money(price)}
      </div>
      {sublabel ? <div className="mt-0.5 text-[10px] text-slate-400 normal-case">{sublabel}</div> : null}
    </th>
  );
}

function TargetYieldCard({ horizon, label, sub, item, fmt, onSetCounter }) {
  if (!item) return null;
  if (!item.converged) {
    return (
      <div
        data-testid={`yield-card-${horizon}`}
        className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-4 text-sm"
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
        <p className="mt-2 text-base font-semibold text-slate-400">Target not achievable</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.reason || "Adjust your inputs or pick a lower target."}</p>
      </div>
    );
  }
  return (
    <div
      data-testid={`yield-card-${horizon}`}
      className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white px-5 py-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-700">{label}</p>
          <p className="mt-1 text-xs text-slate-500">{sub}</p>
        </div>
      </div>
      <p className="mt-3 text-3xl font-bold tabular-nums text-slate-900">{fmt.money(item.price)}</p>
      <p className="mt-1 text-xs text-slate-500">
        Achievable {fmtPct(item.achievable_pct)} · {item.iterations} iterations
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSetCounter(item.price)}
        data-testid={`yield-card-${horizon}-make-counter`}
        className="mt-3 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
      >
        Make this my counter-offer <ArrowRight className="ml-1 h-3 w-3" />
      </Button>
    </div>
  );
}

export default function ScenariosTab({ deal, onDealUpdated }) {
  const fmt = useFmt();
  const [asking, setAsking] = useState(String(deal.purchase_price ?? ""));
  const [counter, setCounter] = useState(String(deal.counter_offer_price ?? ""));
  const [targets, setTargets] = useState(DEFAULT_YIELDS);
  const [selectedTarget, setSelectedTarget] = useState(8);
  const [customTarget, setCustomTarget] = useState("");
  const [data, setData] = useState(deal.scenarios || null);
  const [loading, setLoading] = useState(false);

  const askingNum = Number(asking) || 0;
  const counterNum = Number(counter) || 0;

  const recompute = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const payload = {
        asking_price: askingNum > 0 ? askingNum : undefined,
        counter_offer_price: counterNum > 0 ? counterNum : null,
        target_yields: targets,
        ...overrides,
      };
      const { data } = await api.post(`/deals/${deal.id}/analyze-scenarios`, payload);
      setData(data.scenarios);
      onDealUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run scenarios");
    } finally {
      setLoading(false);
    }
  }, [askingNum, counterNum, targets, deal.id, onDealUpdated]);

  // Auto-run on first mount if we have no saved scenarios.
  useEffect(() => {
    if (!data && deal?.id) recompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id]);

  // Whenever the user picks a new target pill, ensure we have results for it.
  useEffect(() => {
    if (!data) return;
    const have = data.target_yields?.find((r) => Math.abs(r.target_pct - selectedTarget) < 0.001);
    if (!have) {
      // missing — issue an ad-hoc fetch with just that target
      const next = [...new Set([...targets, selectedTarget])];
      setTargets(next);
      recompute({ target_yields: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTarget]);

  const askingKpis = data?.asking?.kpis;
  const counterKpis = data?.counter_offer?.kpis;
  const selectedRow = data?.target_yields?.find((r) => Math.abs(r.target_pct - selectedTarget) < 0.001);

  const compareCols = useMemo(() => {
    const cols = [data?.asking, data?.counter_offer].filter(Boolean);
    // Append the target-yield Y1-5 column when converged.
    if (selectedRow?.y1_5?.converged) {
      cols.push({
        price: selectedRow.y1_5.price,
        kpis: {
          cash_on_cash_pct: selectedRow.y1_5.achievable_pct,
        },
        _synthetic: true,
        _label: `Target ${selectedTarget}%`,
        _sub: "Years 1-5",
      });
    }
    return cols;
  }, [data, selectedRow, selectedTarget]);

  const bestCf = bestColumn(compareCols, "cash_flow_monthly", true);
  const bestCoC = bestColumn(compareCols, "cash_on_cash_pct", true);
  const bestScore = bestColumn(compareCols, "deal_score", true);
  const bestCap = bestColumn(compareCols, "cap_rate", true);
  const bestDscr = bestColumn(compareCols, "dscr", true);

  return (
    <div className="space-y-8" data-testid="scenarios-tab">
      {/* ── A. 3-COL COMPARE ─────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <CalculatorIcon className="h-4 w-4 text-indigo-600" /> Scenario comparison
            </h3>
            <p className="text-sm text-slate-500">Side-by-side asking, counter-offer, and your target-yield price.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Asking</Label>
              <Input
                type="number" min={0}
                value={asking}
                onChange={(e) => setAsking(e.target.value)}
                data-testid="scenarios-asking-input"
                className="h-9 w-32 text-right tabular-nums"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Counter-offer</Label>
              <Input
                type="number" min={0}
                value={counter}
                onChange={(e) => setCounter(e.target.value)}
                placeholder="optional"
                data-testid="scenarios-counter-input"
                className="h-9 w-32 text-right tabular-nums"
              />
            </div>
            <Button
              onClick={() => recompute()}
              disabled={loading}
              data-testid="scenarios-recompute"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null} Recompute
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Metric</th>
                {compareCols.length === 0 ? (
                  <th />
                ) : compareCols.map((c) => {
                  const isAsking = !c._synthetic && c === compareCols.find((x) => !x._synthetic);
                  // Phase 47.3 — PSF subtitle per scenario column.
                  const sqftSub = deal?.property?.square_feet;
                  const scenarioPsf = formatPsfShort(c.price, sqftSub);
                  const baseSub = c._sub || null;
                  const sub = scenarioPsf
                    ? (baseSub ? `${baseSub} · ${scenarioPsf}` : scenarioPsf)
                    : baseSub;
                  return (
                    <CompareHeader
                      // Phase 36 Batch 2 — stable key. Synthetic target
                      // column uses its label; real scenario columns use
                      // `_id` if present, else fall back to price (which
                      // is unique per scenario in this view).
                      key={c._id || c._label || `s-${c.price ?? "x"}`}
                      label={c._synthetic ? c._label : isAsking ? "Asking" : "Counter-offer"}
                      sublabel={sub}
                      price={c.price}
                      fmt={fmt}
                      testid={c._synthetic ? "scenarios-target-col" : isAsking ? "scenarios-asking-col" : "scenarios-counter-col"}
                    />
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && !data ? (
                <tr><td colSpan={compareCols.length + 1} className="px-3 py-12 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
              ) : (
                <>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">Cash flow / mo</td>
                    {compareCols.map((c, i) => (
                      <CompareCell key={c._id || c._label || `s-${c.price ?? i}`} value={c.kpis.cash_flow_monthly} isBest={i === bestCf} formatter={(v) => fmt.money(v, { cents: true })} />
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">NOI / yr</td>
                    {compareCols.map((c, i) => (
                      <CompareCell key={c._id || c._label || `s-${c.price ?? i}`} value={c.kpis.noi_annual} isBest={false} formatter={(v) => fmt.money(v)} />
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">Cap rate</td>
                    {compareCols.map((c, i) => (
                      <CompareCell key={c._id || c._label || `s-${c.price ?? i}`} value={c.kpis.cap_rate} isBest={i === bestCap} formatter={(v) => (v != null ? `${(v * 100).toFixed(2)}%` : "—")} />
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">Cash-on-cash</td>
                    {compareCols.map((c, i) => (
                      <CompareCell key={c._id || c._label || `s-${c.price ?? i}`} value={c.kpis.cash_on_cash_pct} isBest={i === bestCoC} formatter={(v) => fmtPct(v)} />
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">DSCR</td>
                    {compareCols.map((c, i) => (
                      <CompareCell key={c._id || c._label || `s-${c.price ?? i}`} value={c.kpis.dscr} isBest={i === bestDscr} formatter={(v) => (v != null ? v.toFixed(2) : "n/a")} />
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">Deal score</td>
                    {compareCols.map((c, i) => (
                      <CompareCell key={c._id || c._label || `s-${c.price ?? i}`} value={c.kpis.deal_score} isBest={i === bestScore} formatter={(v) => (v != null ? Math.round(v) : "—")} />
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 font-medium text-slate-700">Verdict</td>
                    {compareCols.map((c, i) => (
                      <td key={c._id || c._label || `s-${c.price ?? i}`} className="px-3 py-2.5 text-right">
                        {c.kpis.verdict ? <VerdictBadge verdict={c.kpis.verdict} /> : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── B. TARGET-YIELD PILL CALCULATOR ──────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <Sparkles className="h-4 w-4 text-indigo-600" /> Target yield reverse calculator
            </h3>
            <p className="text-sm text-slate-500">
              Pick a target cash-on-cash and we&apos;ll solve the max price you can pay.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="target-yield-pills">
          {targets.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setSelectedTarget(t)}
              data-testid={`target-yield-${t}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold tracking-wider transition-all ${
                selectedTarget === t
                  ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50"
              }`}
            >
              {t}%
            </button>
          ))}
          <div className="ml-2 flex items-center gap-1">
            <Input
              type="number" min={0} max={50} step={0.5}
              value={customTarget}
              onChange={(e) => setCustomTarget(e.target.value)}
              placeholder="Custom"
              data-testid="target-yield-custom-input"
              className="h-9 w-24 text-sm tabular-nums"
            />
            <Button
              size="sm"
              variant="outline"
              data-testid="target-yield-custom-apply"
              onClick={() => {
                const v = Number(customTarget);
                if (v > 0) { setSelectedTarget(v); setCustomTarget(""); }
              }}
            >
              Set
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2" data-testid="yield-cards">
          <TargetYieldCard
            horizon="y1_5"
            label={`Years 1-5 · ${selectedTarget}% target`}
            sub={`Avg CoC over years 1-5 at ${data?.assumptions?.rent_growth_pct ?? 3}% rent growth`}
            item={selectedRow?.y1_5}
            fmt={fmt}
            onSetCounter={(price) => { setCounter(String(Math.round(price))); toast.success(`Counter-offer set to ${fmt.money(price)}`); }}
          />
          <TargetYieldCard
            horizon="y5_10"
            label={`Years 5-10 · ${selectedTarget}% target`}
            sub="Same CoC but rent has compounded for 5 years"
            item={selectedRow?.y5_10}
            fmt={fmt}
            onSetCounter={(price) => { setCounter(String(Math.round(price))); toast.success(`Counter-offer set to ${fmt.money(price)}`); }}
          />
        </div>

        <p className="flex items-start gap-1.5 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <Info className="mt-0.5 h-3 w-3 flex-shrink-0 text-slate-400" />
          Assumes {data?.assumptions?.rent_growth_pct ?? 3}% annual rent growth and{" "}
          {data?.assumptions?.expense_growth_pct ?? 2.5}% annual expense growth. Adjust via Settings (coming soon).
        </p>
      </div>

      {/* Phase 30 — rent-side reverse-yield calculator (sibling to the price-side card above) */}
      {(deal?.deal_type === "rental" || deal?.deal_type === "multifamily") && (
        <ReverseRentCard deal={deal} />
      )}
    </div>
  );
}
